import {useTranslation} from "react-i18next";
import React, {ReactElement, useCallback, useMemo} from "react";
import FormGroup from "../../../common/form/FormGroup";
import ErrorAlert from "../../../common/components/ErrorAlert";
import FormContainer from "../../../common/form/FormContainer";
import {useForm, useWatch} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useFormSubmission} from "../../../common/servercall/useFormSubmission";
import * as yup from "yup";
import FormFieldBox from "../../../common/form/FormFieldBox";
import FormRow from "../../../common/form/FormRow";
import {CallQueueIdentifier, CallQueueRouting} from "../../../app/types";
import TextInput from "../../../common/form/TextInput";
import {useTableWithAdjustableOrder} from "../../../common/components/table/useTableWithAdjustableOrder";
import {DefaultAutoCompleteOptions} from "../../../common/form/AutocompleteWithAsyncFetch";
import {Paper} from "@mui/material";

const schema = yup.object().shape(
    {
        routingPolicy: yup.string().required('required-f'),
        queuePriorities: yup.array()
    }
)

type Props = {
    callQueueRouting: CallQueueRouting
    onSubmit: (formData: CallQueueRouting) => void
    readonly?: boolean
    buildFormButtons: (isSubmitting: boolean) => ReactElement
}

export const CallQueueRoutingForm = ({callQueueRouting, onSubmit, readonly, buildFormButtons}: Props) => {
    const {t} = useTranslation()

    const routingOptions = useMemo(
        () =>
            ['LONGEST_WAIT_TIME', 'PRIORITY'].map(policy => ({
                value: `${policy}`,
                label: `${t(`settings.call-queue-routing.policies.${policy}`)}`,
            })),
        [t]
    )

    const {control, handleSubmit, setError} = useForm({
        resolver: yupResolver(schema),
        defaultValues: {...callQueueRouting}
    })

    const optionParams = useMemo(() =>
            DefaultAutoCompleteOptions
        , [])

    const formatQueue = useCallback((id: CallQueueIdentifier) => id.name, [])
    const {table: QueueTable} = useTableWithAdjustableOrder<CallQueueIdentifier>('queuePriorities', optionParams, control, formatQueue, id => id.businessId,
        t('users.edit.user-select-button'), false, undefined, true, undefined, true    )

    const {submit, isSubmitting, serverError, onError} = useFormSubmission<CallQueueRouting>(onSubmit, setError)

    const policy = useWatch({
        control,
        name: 'routingPolicy',
    })

    return <>
        <ErrorAlert showAlert={!!serverError}/>
        <FormContainer onSubmit={handleSubmit(submit, onError)}>
            <FormGroup label={' '} fullWidth={true}>
                <FormRow>
                    <FormFieldBox>
                        <TextInput
                            readonly={readonly}
                            label={t('settings.call-queue-routing.policy')}
                            name={'routingPolicy'}
                            control={control}
                            required={false}
                            options={routingOptions}
                            isRadio={true}
                        />
                    </FormFieldBox>
                </FormRow>
                {
                    policy === 'PRIORITY' &&
                        <FormRow>
                            <FormFieldBox>
                                <Paper elevation={2}>
                                    <QueueTable/>
                                </Paper>
                            </FormFieldBox>
                        </FormRow>
                }

            </FormGroup>
            {buildFormButtons(isSubmitting)}
        </FormContainer>
    </>
}
